import { Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import { ApiError } from '~/api/client'
import { reached } from '~/components/use-work'
import { ConfirmDialog } from '~/components/confirm-dialog'
import { PanelHeader } from '~/components/panel-header'
import { CycleSentence, CycleWeeks } from '~/components/cycle-weeks'
import { Dialog } from '~/components/dialog'
import { DialogLink } from '~/components/dialog-link'
import {
  CopyIcon,
  DeleteIcon,
  EditIcon,
  ExploreIcon,
  PlayIcon,
  PlusIcon,
  PublishIcon,
  UnpublishIcon,
} from '~/components/icons'
import { Skeleton, SkeletonBlock } from '~/components/skeleton'
import { StartRunDialog } from '~/components/start-run-dialog'
import { WorkingButton } from '~/components/working'
import { useDataService, useDataState } from '~/data/data-context'
import {
  openableTemplateId,
  parseTemplate,
  type ActiveTemplate,
  type Enrollment,
  type WorkoutTemplate,
} from '~/data/templates'
import { nextWorkoutPhrase } from '~/lib/active-phrase'
import { withoutDialog } from '~/lib/dialogs'
import { scheduleOf, strictNode } from '~/lib/template-tree'
import { weekdayNameOf, weekdayOf } from '~/lib/weekdays'

/**
 * Which weekday a run's cycle begins on. A run started before anybody was asked has no answer
 * of its own and counts from the day it began, so that is the weekday it is shown as — saying
 * "the week's first day" there would be describing a run other than the one on screen.
 */
function anchorOf(enrollment: Enrollment): number {
  return enrollment.anchorDay ?? weekdayOf(enrollment.startedAt)
}

/** The line under a row's name: what kind of schedule it runs, and how much it holds. */
function describeTemplate(template: WorkoutTemplate): string {
  try {
    const node = parseTemplate(template.script)
    const count = node.workouts.length
    return `${scheduleOf(node)} · ${String(count)} ${count === 1 ? 'workout' : 'workouts'}`
  } catch {
    return 'The script does not parse'
  }
}

/**
 * The templates panel, in the corner card: the run you are on now, then the library as
 * compact rows. Runs of one begin and end here; a template's own page is the editor, and
 * what everybody else published is behind Explore.
 */
export function TemplatesPanel({ onBack }: { onBack: () => void }) {
  const state = useDataState()
  const service = useDataService()
  const navigate = useNavigate()

  // Opened on the answer the data layer already holds, so returning here does not wait again.
  const [kept] = useState(() => service.heldActiveTemplate())
  const [active, setActive] = useState<ActiveTemplate | null>(kept?.value ?? null)
  const [looked, setLooked] = useState(kept !== null)
  const [ending, setEnding] = useState<Enrollment | null>(null)
  const [deleting, setDeleting] = useState<WorkoutTemplate | null>(null)
  const [starting, setStarting] = useState<WorkoutTemplate | null>(null)
  const [anchoring, setAnchoring] = useState<number | null>(null)
  const [ended, setEnded] = useState<Enrollment | null>(null)
  const [unpublishing, setUnpublishing] = useState<WorkoutTemplate | null>(null)
  const [trouble, setTrouble] = useState<string | null>(null)

  const running = state.enrollments.find((enrollment) => enrollment.endedAt === null) ?? null

  // The active template's next workout is worked out from its history, so it is fetched
  // rather than guessed — and again whenever the run or the script changes.
  useEffect(() => {
    let alive = true
    void service.activeTemplate().then((found) => {
      if (alive) {
        setActive(found)
        setLooked(true)
      }
    })
    return () => {
      alive = false
    }
    // The anchor is in here because moving it moves which workout today is, and the answer
    // above is the one that says so.
  }, [service, running?.id, running?.anchorDay, state.templates])

  /*
   * The version Explore offers, when it is not the one this run follows. Only for a template
   * somebody else published: your own moves on the moment you save it.
   */
  const newerVersion =
    active !== null &&
    active.enrollment.mine !== true &&
    active.enrollment.listedVersionId != null &&
    active.enrollment.listedVersionId !== active.enrollment.versionId
      ? active.enrollment.listedVersionId
      : null

  async function moveOn(enrollmentId: string, versionId: string): Promise<void> {
    setTrouble(null)
    try {
      await service.moveRunToVersion(enrollmentId, versionId)
    } catch {
      setTrouble('Updating needs a connection.')
    }
  }

  /*
   * Starting ends the run underway, so it asks while there is one to end; a strict schedule has
   * to be asked which weekday its cycle begins on either way. With neither to ask, nothing
   * stands between the press and the run.
   */
  function beginRun(template: WorkoutTemplate): void {
    if (running === null && strictNode(template.script) === null) {
      void service.enroll(template.id, null)
      return
    }
    setStarting(template)
  }

  async function copyRunningTemplate(): Promise<void> {
    if (active === null) {
      return
    }
    // The script is already on the device — it is what the run has been planning from.
    const copy = await service.saveTemplate(null, active.script, active.enrollment.versionId)
    // A copy is yours from here on, so it opens where you would change it.
    await navigate({
      to: '/templates/$templateId/edit',
      params: { templateId: copy.id },
      search: withoutDialog,
    })
  }

  async function publish(template: WorkoutTemplate): Promise<void> {
    setTrouble(null)
    try {
      await service.publishTemplate(template.id)
    } catch (error) {
      setTrouble(
        error instanceof ApiError
          ? `${template.name} will not publish: ${error.message}`
          : 'Publishing needs a connection.',
      )
    }
  }

  return (
    <>
      <PanelHeader title="Templates" close onBack={onBack} />
      <div className="w-104 space-y-4 px-4 pb-4">
        <section aria-label="Active" className="rounded-2xl bg-surface-raised p-4">
          {!looked ? (
            // The shape of what is coming: a name, the line under it, and its controls.
            <SkeletonBlock label="Loading your training" className="space-y-2">
              <Skeleton className="h-6 w-44" />
              <Skeleton className="h-5 w-32" />
              <Skeleton className="mt-3 h-9 w-24 rounded-full" />
            </SkeletonBlock>
          ) : active === null ? (
            <p className="text-sm text-ink-muted">
              No active template. Start one and it decides what you do next.
            </p>
          ) : active.error !== null ? (
            <p className="text-sm text-ink-muted">
              {active.name} will not run: {active.error}
            </p>
          ) : (
            <article className="space-y-1">
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  {/* The name opens the template's page — what it is, and what you have done
                      of it. A run of a template nobody can reach any more opens nothing: not
                      deleted alone, but deleted or unpublished and never yours. */}
                  {openableTemplateId(active.enrollment, state.templates) === null ? (
                    <h3 className="truncate text-lg font-medium">{active.name}</h3>
                  ) : (
                    <h3 className="truncate text-lg font-medium">
                      <DialogLink
                        dialog="template"
                        templateId={openableTemplateId(active.enrollment, state.templates) ?? ''}
                        className="rounded-md transition-colors hover:text-accent"
                      >
                        {active.name}
                      </DialogLink>
                    </h3>
                  )}
                  <p className="text-sm text-ink-muted">
                    {nextWorkoutPhrase(active) ??
                      (active.next !== null ? `Next: ${active.next.name}` : 'Nothing due')}
                  </p>
                </div>
                {/*
                  A template of your own is edited from here; somebody else's is not yours to
                  edit, so the way on is a copy of it that is.
                */}
                {active.template !== null ? (
                  <Link
                    to="/templates/$templateId/edit"
                    search={withoutDialog}
                    params={{ templateId: active.template.id }}
                    aria-label="Edit template"
                    className="rounded-full p-2 text-ink-muted transition-colors hover:bg-hover hover:text-ink"
                  >
                    <EditIcon aria-hidden className="size-6" />
                  </Link>
                ) : (
                  <button
                    type="button"
                    aria-label="Copy to your library"
                    className="rounded-full p-2 text-ink-muted transition-colors hover:bg-hover hover:text-ink"
                    onClick={() => {
                      void copyRunningTemplate()
                    }}
                  >
                    <CopyIcon aria-hidden className="size-6" />
                  </button>
                )}
              </div>
              {active.enrollment.authorName != null && active.enrollment.mine !== true ? (
                <p className="text-sm text-ink-muted">by {active.enrollment.authorName}</p>
              ) : null}
              {/*
                Where the cycle sits on the calendar is the other half of when the run began,
                so it is said in the same breath — and changed from here.
              */}
              {active.node !== null && scheduleOf(active.node) === 'strict' ? (
                <p className="text-sm text-ink-muted">
                  Cycle begins {weekdayNameOf(anchorOf(active.enrollment))}
                  <button
                    type="button"
                    className="pl-2 font-medium text-accent"
                    onClick={() => {
                      setAnchoring(anchorOf(active.enrollment))
                    }}
                  >
                    Change
                  </button>
                </p>
              ) : null}
              {/*
                A run of somebody else's template never moves on its own. It says an update is
                there, and moving on is a press — the run stays, so its progressions carry on.
              */}
              {newerVersion !== null ? (
                <div className="mt-1 flex flex-wrap items-center gap-3 rounded-xl bg-surface-bright p-2.5">
                  <span className="flex-1 text-sm">A newer version has been published</span>
                  <WorkingButton
                    className="text-sm font-medium text-accent"
                    onPress={() => moveOn(active.enrollment.id, newerVersion)}
                  >
                    Update
                  </WorkingButton>
                </div>
              ) : null}
              <div className="pt-2">
                <button
                  type="button"
                  className="rounded-full border border-outline px-4 py-1.5 text-sm font-medium transition-colors hover:bg-hover"
                  onClick={() => {
                    setEnding(active.enrollment)
                  }}
                >
                  End
                </button>
              </div>
            </article>
          )}
        </section>

        <section aria-label="Library">
          <div className="flex items-center gap-1 pb-1">
            <h3 className="flex-1 px-1 text-sm font-medium">Library</h3>
            {/* The door out to what everybody else has written sits beside your own shelf. */}
            <DialogLink
              dialog="explore"
              label="Explore"
              className="rounded-full p-2 text-ink-muted transition-colors hover:bg-hover hover:text-ink"
            >
              <ExploreIcon aria-hidden className="size-6" />
            </DialogLink>
            {/* New templates are made from the library, so that is where the door is. */}
            <Link
              to="/templates/$templateId/edit"
              search={withoutDialog}
              params={{ templateId: 'new' }}
              aria-label="New template"
              className="rounded-full p-2 text-ink-muted transition-colors hover:bg-hover hover:text-ink"
            >
              <PlusIcon aria-hidden className="size-6" />
            </Link>
          </div>
          {trouble !== null ? <p className="px-1 pb-2 text-sm text-danger">{trouble}</p> : null}
          {state.templates.length === 0 ? (
            <p className="px-1 py-2 text-sm text-ink-muted">
              Nothing saved yet. Write one and it lands here.
            </p>
          ) : (
            <ul>
              {state.templates.map((template) => (
                <li
                  key={template.id}
                  className="group relative flex items-center gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-hover"
                >
                  <TemplateMark name={template.name} />
                  <div className="min-w-0 flex-1">
                    {/* The name is the way into the template's page, and it reaches the row. */}
                    <DialogLink
                      dialog="template"
                      templateId={template.id}
                      className="block truncate font-medium after:absolute after:inset-0 after:rounded-xl"
                    >
                      {template.name}
                    </DialogLink>
                    <p className="truncate text-sm text-ink-muted">
                      {describeTemplate(template)}
                      {template.publishedVersionId != null ? (
                        <span className="pl-2">
                          {template.publishedIsCurrent === true ? 'Published' : 'Edits unpublished'}
                        </span>
                      ) : null}
                      {template.id === running?.templateId ? (
                        <span className="pl-2 text-accent">On it now</span>
                      ) : null}
                    </p>
                  </div>
                  {/* The row's controls sit above its reach, and the quieter ones wait for a
                      pointer that is over the row or a focus that has landed inside it. */}
                  <div className="relative z-10 flex shrink-0 items-center">
                    <WorkingButton
                      spinner="size-6"
                      /*
                        Two states, not three: published or not. Publishing an edit is the
                        editor's job — see the templates page this panel grew out of.
                      */
                      aria-label={
                        template.publishedVersionId == null
                          ? `Publish ${template.name}`
                          : `Unpublish ${template.name}`
                      }
                      // Working, it stands whether or not the pointer is over its row.
                      className="rounded-full p-1.5 text-ink-muted opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 hover:bg-hover-strong hover:text-ink aria-busy:opacity-100"
                      onPress={async () => {
                        if (template.publishedVersionId != null) {
                          setUnpublishing(template)
                        } else {
                          await publish(template)
                        }
                      }}
                    >
                      {template.publishedVersionId == null ? (
                        <PublishIcon aria-hidden className="size-6" />
                      ) : (
                        <UnpublishIcon aria-hidden className="size-6" />
                      )}
                    </WorkingButton>
                    <button
                      type="button"
                      aria-label={`Delete ${template.name}`}
                      className="rounded-full p-1.5 text-ink-muted opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 hover:bg-hover-strong hover:text-ink"
                      onClick={() => {
                        setDeleting(template)
                      }}
                    >
                      <DeleteIcon aria-hidden className="size-6" />
                    </button>
                    <button
                      type="button"
                      aria-label={`Start ${template.name}`}
                      className="ml-1 grid size-9 place-items-center rounded-full bg-surface-bright text-accent transition-colors hover:bg-hover-strong"
                      onClick={() => {
                        beginRun(template)
                      }}
                    >
                      <PlayIcon aria-hidden className="size-6" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Ending a run can be undone for a moment after — the same run resumes. */}
      {ended !== null ? (
        <div className="fixed inset-x-0 bottom-4 z-50 mx-auto flex w-full max-w-md items-center gap-3 rounded-2xl bg-surface-bright p-3 shadow-lg">
          <span className="flex-1 text-sm">{ended.templateName} ended</span>
          <button
            type="button"
            className="text-sm font-medium text-accent"
            onClick={() => {
              const held = ended
              setEnded(null)
              void service.resumeEnrollment(held.id)
            }}
          >
            Undo
          </button>
        </div>
      ) : null}

      {ending !== null ? (
        <ConfirmDialog
          title="End this run?"
          body={`${ending.templateName} stops deciding what you do next. Starting it again later begins a fresh run, and its progressions start over.`}
          confirm="End run"
          onConfirm={() => {
            const held = ending
            setEnding(null)
            void service.endEnrollment(held.id).then(() => {
              setEnded(held)
            })
          }}
          onDismiss={() => {
            setEnding(null)
          }}
        />
      ) : null}

      {deleting !== null ? (
        <ConfirmDialog
          title={`Delete ${deleting.name}?`}
          body="The template goes. The runs of it stay in your history, and so do the workouts you logged from it."
          confirm="Delete"
          onConfirm={() => {
            const held = deleting
            setDeleting(null)
            void service.deleteTemplate(held.id)
          }}
          onDismiss={() => {
            setDeleting(null)
          }}
        />
      ) : null}

      {unpublishing !== null ? (
        <ConfirmDialog
          title={`Unpublish ${unpublishing.name}?`}
          body="It leaves Explore, and nobody new can start it. Everybody already running it carries on, on the version they are on."
          confirm="Unpublish"
          onConfirm={async () => {
            await reached(
              () => service.unpublishTemplate(unpublishing.id),
              'Unpublishing needs a connection.',
            )
            setTrouble(null)
            setUnpublishing(null)
          }}
          onDismiss={() => {
            setUnpublishing(null)
          }}
        />
      ) : null}

      {starting !== null ? (
        <StartRunDialog
          name={starting.name}
          ending={running === null ? null : running.templateName || 'The run you are on now'}
          strict={strictNode(starting.script)}
          onStart={(anchorDay) => {
            const held = starting
            setStarting(null)
            void service.enroll(held.id, anchorDay)
          }}
          onDismiss={() => {
            setStarting(null)
          }}
        />
      ) : null}

      {anchoring !== null && active !== null ? (
        // Wide enough that a workout's name fits in a column of the week it is drawn on.
        <Dialog
          label="Which weekday the cycle begins on"
          className="max-w-2xl"
          onDismiss={() => {
            setAnchoring(null)
          }}
        >
          <h2 className="text-lg font-medium">Which weekday the cycle begins on</h2>
          {active.node !== null ? <CycleSentence node={active.node} /> : null}
          {active.node !== null ? (
            <CycleWeeks node={active.node} anchor={anchoring} onAnchor={setAnchoring} />
          ) : null}
          {/* The cost of the choice, under the choice — read before pressing Save, not after. */}
          <p className="text-sm text-ink-muted">
            The cycle is counted from the day the run began, so this can change what {active.name}{' '}
            offers today. Nothing you have logged moves.
          </p>
          <div className="flex flex-wrap justify-end gap-3">
            <button
              type="button"
              className="rounded-full px-4 py-2 text-sm"
              onClick={() => {
                setAnchoring(null)
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-full bg-accent px-4 py-2 text-sm font-medium text-on-accent"
              onClick={() => {
                const chosen = anchoring
                setAnchoring(null)
                void service.setRunAnchor(active.enrollment.id, chosen)
              }}
            >
              Save
            </button>
          </div>
        </Dialog>
      ) : null}
    </>
  )
}

/**
 * The mark a template wears beside its row: the letter it starts with, on a raised square.
 * It stays grey — the accent marks the primary action and focus, and nothing else.
 */
export function TemplateMark({ name, className }: { name: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={`grid size-9 shrink-0 place-items-center rounded-lg bg-surface-bright font-medium ${className ?? ''}`}
    >
      {name.trim().slice(0, 1).toUpperCase() || '?'}
    </span>
  )
}
